/**
 * Org chart tree helpers for workspace-scoped hierarchy display.
 *
 * Product model (canon 10P): workspace = organization object; org_units = chart only.
 * Legacy workspaces may still have a duplicate `workspace-queue` root until migration 514 runs.
 */
import type { OrgUnit } from '../types/prioritization/enterpriseIdentity';

export interface OrgUnitTreeNode {
  unit: OrgUnit;
  children: OrgUnitTreeNode[];
}

export const LEGACY_WORKSPACE_QUEUE_NAME = 'workspace-queue';

/** Active org units scoped to the current workspace (excludes foreign null-workspace rows). */
export function filterOrgUnitsForWorkspace(
  units: OrgUnit[],
  workspaceId: string | null | undefined,
): OrgUnit[] {
  const active = units.filter((u) => u.active);
  if (!workspaceId) return active;
  return active.filter((u) => !u.workspaceId || u.workspaceId === workspaceId);
}

function attachOrphanUnitsUnderCanonicalRoot(
  units: OrgUnit[],
  workspaceId: string | null | undefined,
): OrgUnit[] {
  if (!workspaceId || units.length === 0) return units;

  const canonical = getCanonicalWorkspaceOrgRoot(units, workspaceId);
  if (!canonical) return units;

  const idSet = new Set(units.map((u) => u.id));
  return units.map((u) => {
    if (u.id === canonical.id) return u;
    const parentId = resolveParentId(u, idSet);
    if (parentId) return u;

    // Department/team rows created without a parent — show under the org root for cycle scope.
    return {
      ...u,
      parentId: canonical.id,
      path: [canonical.id, u.id],
      depth: Math.max((canonical.depth ?? 0) + 1, u.depth > 0 ? u.depth : 1),
    };
  });
}

/** Org chart nodes for cycle scope and lane pickers (workspace filter + legacy merge + org root). */
export function filterOrgUnitsForCycleScope(
  units: OrgUnit[],
  workspaceId: string | null | undefined,
): OrgUnit[] {
  const scoped = filterOrgUnitsForWorkspace(units, workspaceId);
  const normalized = normalizeOrgUnitsForHierarchyDisplay(scoped);
  return attachOrphanUnitsUnderCanonicalRoot(normalized, workspaceId);
}

/** Canonical depth-0 org root for intake default routing and cycle scope. */
export function getCanonicalWorkspaceOrgRoot(
  units: OrgUnit[],
  workspaceId: string,
): OrgUnit | null {
  const scoped = normalizeOrgUnitsForHierarchyDisplay(
    filterOrgUnitsForWorkspace(units, workspaceId),
  );
  const idSet = new Set(scoped.map((u) => u.id));
  const roots = scoped
    .filter((u) => !resolveParentId(u, idSet))
    .filter((u) => u.name !== LEGACY_WORKSPACE_QUEUE_NAME);

  if (roots.length === 0) return null;

  // The root is the topmost node, whatever it is called. `type` used to be a
  // requirement here ("only a depth-0 `organization` node"), which meant a
  // perfectly good hierarchy whose top node happened to be typed `department`
  // resolved to no root at all — and the Cycle Designer then anchored on an
  // arbitrary unit via its own `?? scoped[0]` fallback. Type is a label; the
  // shape of the tree is the truth.
  //
  // A single root is the normal case and needs no tie-break. When a workspace
  // has several (an org hierarchy that was never given a top), prefer the
  // shallowest, then an `organization`-typed one, then the earliest by name so
  // the choice is at least stable across reloads.
  if (roots.length === 1) return roots[0]!;

  return [...roots].sort((a, b) => {
    const depthDiff = (a.depth ?? 0) - (b.depth ?? 0);
    if (depthDiff !== 0) return depthDiff;
    const aOrg = a.type === 'organization' ? 0 : 1;
    const bOrg = b.type === 'organization' ? 0 : 1;
    if (aOrg !== bOrg) return aOrg - bOrg;
    return (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name);
  })[0]!;
}

export function resolveParentId(unit: OrgUnit, idSet: Set<string>): string | undefined {
  if (unit.parentId && idSet.has(unit.parentId)) return unit.parentId;
  if (unit.path?.length) {
    for (let i = unit.path.length - 2; i >= 0; i--) {
      const ancestorId = unit.path[i];
      if (ancestorId !== unit.id && idSet.has(ancestorId)) return ancestorId;
    }
  }
  return undefined;
}

/**
 * After a reparent (or any parentId change), recompute `path` + `depth` for
 * `unitId` and every descendant from live parentId links.
 *
 * Settings hierarchy trees key off parentId; Design Paths historically keyed
 * off stored depth — without this cascade, reparented nodes look correct in
 * Org Hierarchy but stay flat/stale in the Cycle Designer.
 */
export function cascadeOrgUnitPathAndDepth(
  units: Map<string, OrgUnit>,
  unitId: string,
): Map<string, OrgUnit> {
  const next = new Map(units);
  const unit = next.get(unitId);
  if (!unit) return next;

  const parent = unit.parentId ? next.get(unit.parentId) : undefined;
  const path = parent ? [...(parent.path ?? [parent.id]), unitId] : [unitId];
  const depth = parent ? (parent.depth ?? 0) + 1 : 0;
  next.set(unitId, { ...unit, path, depth });

  const queue = [unitId];
  const seen = new Set<string>([unitId]);
  while (queue.length > 0) {
    const parentId = queue.shift()!;
    const parentUnit = next.get(parentId);
    if (!parentUnit) continue;
    for (const [id, child] of next) {
      if (child.parentId !== parentId || seen.has(id)) continue;
      seen.add(id);
      next.set(id, {
        ...child,
        path: [...(parentUnit.path ?? [parentId]), id],
        depth: (parentUnit.depth ?? 0) + 1,
      });
      queue.push(id);
    }
  }
  return next;
}

/**
 * Legacy shim: fold workspace-queue roots under the organization root for display.
 * No-op once migration 514 has merged duplicates in the database.
 */
export function normalizeOrgUnitsForHierarchyDisplay(units: OrgUnit[]): OrgUnit[] {
  if (units.length === 0) return units;

  const idSet = new Set(units.map((u) => u.id));
  const roots = units.filter((u) => !resolveParentId(u, idSet));
  const workspaceQueueRoots = roots.filter((u) => u.name === LEGACY_WORKSPACE_QUEUE_NAME);
  const otherRoots = roots.filter((u) => u.name !== LEGACY_WORKSPACE_QUEUE_NAME);

  if (workspaceQueueRoots.length === 0 || otherRoots.length === 0) {
    return units.filter((u) => u.active);
  }

  const canonicalRoot =
    otherRoots.find((u) => u.type === 'organization') ?? otherRoots[0];
  const queueRootIds = new Set(workspaceQueueRoots.map((u) => u.id));

  return units
    .filter((u) => u.active && !queueRootIds.has(u.id))
    .map((u) => {
      const parentId = resolveParentId(u, idSet);
      if (parentId && queueRootIds.has(parentId)) {
        return { ...u, parentId: canonicalRoot.id };
      }
      if (u.parentId && queueRootIds.has(u.parentId)) {
        return { ...u, parentId: canonicalRoot.id };
      }
      return u;
    });
}

export function resolveOrgScopeNodeId(nodeId: string, units: OrgUnit[]): string {
  if (!nodeId) return nodeId;
  const normalized = normalizeOrgUnitsForHierarchyDisplay(units.filter((u) => u.active));
  if (normalized.some((u) => u.id === nodeId)) return nodeId;

  const selected = units.find((u) => u.id === nodeId);
  if (selected?.name === LEGACY_WORKSPACE_QUEUE_NAME) {
    const idSet = new Set(units.map((u) => u.id));
    const roots = units.filter((u) => !resolveParentId(u, idSet));
    const otherRoots = roots.filter((u) => u.name !== LEGACY_WORKSPACE_QUEUE_NAME);
    const canonical =
      otherRoots.find((u) => u.type === 'organization') ?? otherRoots[0];
    if (canonical) return canonical.id;
  }
  return nodeId;
}

/**
 * Org units where Catchball working-tier participants can be assigned.
 *
 * Returns only the DIRECT CHILDREN of the cycle scope node — never the scope
 * node itself. The cycle owner operates at the scope level; the ball always
 * travels DOWN to the next level, so the scope root is not a valid recipient.
 */
export function getWorkingTierAssignableOrgUnits(
  orgScopeNodeId: string | null | undefined,
  units: OrgUnit[],
): OrgUnit[] {
  if (!orgScopeNodeId) return [];

  const active = units.filter((u) => u.active);
  const idSet = new Set(active.map((u) => u.id));
  const scopeNode = active.find((u) => u.id === orgScopeNodeId);
  if (!scopeNode) return [];

  return active
    .filter((u) => {
      if (u.id === orgScopeNodeId) return false;
      return resolveParentId(u, idSet) === orgScopeNodeId;
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function buildOrgUnitTree(units: OrgUnit[]): OrgUnitTreeNode[] {
  const normalized = normalizeOrgUnitsForHierarchyDisplay(units.filter((u) => u.active));
  return buildTreeFromPreparedUnits(normalized);
}

/**
 * Breadcrumb label for an org unit: "Org / Dept / Team".
 * Falls back to displayName when path ancestors are missing from the map.
 */
export function formatOrgUnitPathLabel(
  unit: OrgUnit,
  byId: Map<string, OrgUnit> | ReadonlyMap<string, OrgUnit>,
): string {
  const parts: string[] = [];
  if (unit.path?.length) {
    for (const id of unit.path) {
      const node = byId.get(id);
      if (node) parts.push(node.displayName || node.name);
    }
  }
  if (parts.length === 0) {
    let cur: OrgUnit | undefined = unit;
    const seen = new Set<string>();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      parts.unshift(cur.displayName || cur.name);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
  }
  if (parts.length === 0) return unit.displayName || unit.name;
  // Avoid repeating the leaf when path already ends with it
  const leaf = unit.displayName || unit.name;
  if (parts[parts.length - 1] !== leaf) parts.push(leaf);
  return parts.join(' / ');
}

/** Cycle org-scope picker tree — full 10P pipeline (workspace filter, legacy merge, org root). */
export function buildOrgUnitTreeForCycleScope(
  units: OrgUnit[],
  workspaceId: string | null | undefined,
): OrgUnitTreeNode[] {
  const prepared = filterOrgUnitsForCycleScope(units, workspaceId);
  return buildTreeFromPreparedUnits(prepared);
}

function buildTreeFromPreparedUnits(prepared: OrgUnit[]): OrgUnitTreeNode[] {
  if (prepared.length === 0) return [];

  const idSet = new Set(prepared.map((u) => u.id));
  const nodeMap = new Map<string, OrgUnitTreeNode>();
  prepared.forEach((u) => nodeMap.set(u.id, { unit: u, children: [] }));

  const roots: OrgUnitTreeNode[] = [];
  prepared.forEach((u) => {
    const node = nodeMap.get(u.id)!;
    const parentId = resolveParentId(u, idSet);
    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: OrgUnitTreeNode[]) => {
    nodes.sort((a, b) => a.unit.displayName.localeCompare(b.unit.displayName));
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}
